// apps/web/src/pages/vendas/importar-page.tsx — Fase 12, Plano 25 (PV-03, D-30..D-32).
// Assistente "Importar pedido de compra": 1 Arquivo → 2 Mapeamento → 3 Prévia → confirmação.
// O navegador só envia arquivo + mapeamento + flags; a confirmação reprocessa o arquivo no servidor (T-12-27).
// Todo o estado vive aqui: erro da API nunca descarta arquivo, mapeamento nem prévia, e "Voltar" não perde nada.
import { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import type {
  CampoImportacaoPv,
  ClienteLookupPv,
  ConfirmarImportacaoInput,
  LeituraPlanilha,
  LerPlanilhaInput,
  PreviaImportacao,
  ResultadoImportacao,
} from '@pieltec/contracts';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { apiMutate } from '../../lib/csrf-fetch';
import { CODIGO_IMPORTACAO_JA_CONFIRMADA, lerErroApi, type PvJaImportado } from '../../lib/vendas/erro-api';
import {
  ERRO_ARQUIVO_GRANDE,
  ERRO_ARQUIVO_INVALIDO,
  LIMITE_ARQUIVO_BYTES,
  colunasIniciais,
  classificarFalhaConfirmacao,
  mensagemFalhaConfirmacao,
  montarMapeamento,
  podeConfirmar,
  rotuloSucessoImportacao,
  type ColunasEscolhidas,
} from '../../lib/vendas/importacao';
import { PassoArquivo } from './importacao/passo-arquivo';
import { PassoMapeamento } from './importacao/passo-mapeamento';
import { PassoPrevia } from './importacao/passo-previa';

type Passo = 1 | 2 | 3;

const PASSOS: ReadonlyArray<{ numero: Passo; titulo: string }> = [
  { numero: 1, titulo: 'Arquivo' },
  { numero: 2, titulo: 'Mapeamento' },
  { numero: 3, titulo: 'Prévia' },
];

class ErroImportacao extends Error {
  constructor(
    readonly status: number,
    readonly detalhe: string | null,
    readonly codigo: string | null = null,
    readonly jaImportado: readonly PvJaImportado[] = [],
  ) {
    super(`HTTP ${status}`);
  }
}

function lerBase64(arquivo: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onload = () => {
      const resultado = typeof leitor.result === 'string' ? leitor.result : '';
      resolve(resultado.slice(resultado.indexOf(',') + 1));
    };
    leitor.onerror = () => reject(new Error('leitura'));
    leitor.readAsDataURL(arquivo);
  });
}

async function chamar<T>(caminho: string, corpo: unknown): Promise<T> {
  const resposta = await apiMutate(`/api/v1/vendas/importacoes/${caminho}`, { body: corpo });
  if (!resposta.ok) {
    const erro = await lerErroApi(resposta, '');
    throw new ErroImportacao(resposta.status, resposta.status !== 403 && erro.mensagem ? erro.mensagem : null, erro.codigo, erro.jaImportado);
  }
  return (await resposta.json()) as T;
}

function mensagemDe(erro: unknown, padrao: string): string {
  return erro instanceof ErroImportacao && erro.status === 403 ? 'Você não tem permissão para esta ação.' : padrao;
}

export function ImportarPage() {
  const [passo, setPasso] = useState<Passo>(1);
  const [cliente, setCliente] = useState<ClienteLookupPv | null>(null);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [base64, setBase64] = useState('');
  const [temCabecalho, setTemCabecalho] = useState(true);
  const [abas, setAbas] = useState<readonly string[]>([]);
  const [aba, setAba] = useState<string | null>(null);
  const [leitura, setLeitura] = useState<LeituraPlanilha | null>(null);
  const [chaveLida, setChaveLida] = useState('');
  const [colunas, setColunas] = useState<ColunasEscolhidas>({});
  const [mapeamentoAplicado, setMapeamentoAplicado] = useState(false);
  const [salvar, setSalvar] = useState(true);
  const [previa, setPrevia] = useState<PreviaImportacao | null>(null);
  const [ignorarErros, setIgnorarErros] = useState(false);
  const [apenasProblemas, setApenasProblemas] = useState(false);
  const [resultado, setResultado] = useState<ResultadoImportacao | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [detalheErro, setDetalheErro] = useState<string | null>(null);
  /** PVs que a mesma planilha já criou (409 IMPORTACAO_JA_CONFIRMADA). */
  const [pvsJaCriados, setPvsJaCriados] = useState<readonly PvJaImportado[]>([]);
  /** Resultado incerto (rede/5xx): convite a conferir a lista de pedidos antes de tentar de novo. */
  const [conferirPedidos, setConferirPedidos] = useState(false);

  function chaveAtual(): string {
    if (!cliente || !arquivo) return '';
    return [cliente.id, arquivo.name, arquivo.size, arquivo.lastModified, aba ?? '', temCabecalho].join('|');
  }

  function entrada(): LerPlanilhaInput | null {
    if (!cliente || !arquivo || base64 === '') return null;
    return { clienteId: cliente.id, nomeArquivo: arquivo.name, arquivoBase64: base64, aba, temCabecalho };
  }

  function limparErro(): void {
    setErro(null);
    setDetalheErro(null);
    setPvsJaCriados([]);
    setConferirPedidos(false);
  }

  /** Trocar cliente, arquivo, aba ou cabeçalho invalida leitura, mapeamento e prévia já obtidos. */
  function invalidarLeitura(): void {
    setLeitura(null);
    setChaveLida('');
    setPrevia(null);
    setIgnorarErros(false);
  }

  async function escolherArquivo(novo: File | null): Promise<void> {
    limparErro();
    invalidarLeitura();
    setAbas([]);
    setAba(null);
    setColunas({});
    setArquivo(null);
    setBase64('');
    if (!novo) return;
    if (novo.size > LIMITE_ARQUIVO_BYTES) {
      setErro(ERRO_ARQUIVO_GRANDE);
      return;
    }
    try {
      setBase64(await lerBase64(novo));
      setArquivo(novo);
    } catch {
      setErro(ERRO_ARQUIVO_INVALIDO);
    }
  }

  async function continuarParaMapeamento(): Promise<void> {
    const base = entrada();
    if (!base) return;
    const chave = chaveAtual();
    if (leitura && chave === chaveLida) {
      setPasso(2);
      return;
    }
    setOcupado(true);
    limparErro();
    try {
      const lida = await chamar<LeituraPlanilha>('ler', base);
      setAbas(lida.abas);
      setAba(lida.aba);
      if (lida.abas.length > 1 && base.aba == null) {
        // Várias abas: o usuário confirma qual usar antes de mapear.
        invalidarLeitura();
        return;
      }
      setLeitura(lida);
      setChaveLida([cliente?.id, arquivo?.name, arquivo?.size, arquivo?.lastModified, lida.aba ?? '', temCabecalho].join('|'));
      setColunas(colunasIniciais(lida.mapeamentoSalvo));
      setMapeamentoAplicado(lida.mapeamentoSalvo !== null);
      setPrevia(null);
      setPasso(2);
    } catch (e) {
      setErro(mensagemDe(e, ERRO_ARQUIVO_INVALIDO));
      setDetalheErro(e instanceof ErroImportacao && e.status === 400 ? e.detalhe : null);
    } finally {
      setOcupado(false);
    }
  }

  async function gerarPrevia(): Promise<void> {
    const base = entrada();
    if (!base || !leitura) return;
    setOcupado(true);
    limparErro();
    try {
      setPrevia(await chamar<PreviaImportacao>('previa', { ...base, mapeamento: montarMapeamento(colunas, leitura.cabecalhos, temCabecalho) }));
      setIgnorarErros(false);
      setApenasProblemas(false);
      setPasso(3);
    } catch (e) {
      setErro(mensagemDe(e, e instanceof ErroImportacao && e.detalhe ? e.detalhe : 'Não foi possível gerar a prévia. Confira o mapeamento e tente novamente.'));
    } finally {
      setOcupado(false);
    }
  }

  async function confirmar(): Promise<void> {
    const base = entrada();
    if (!base || !leitura || !previa) return;
    const itensValidos = previa.resumo.itens - previa.resumo.comErro;
    // Nunca confirma sem prévia válida (defesa em profundidade; o botão já fica desabilitado).
    if (!podeConfirmar({ comErro: previa.resumo.comErro, ignorarErros, itensValidos })) return;
    setOcupado(true);
    limparErro();
    try {
      const corpo: ConfirmarImportacaoInput = {
        ...base,
        mapeamento: montarMapeamento(colunas, leitura.cabecalhos, temCabecalho),
        ignorarErros,
        salvarMapeamento: salvar,
      };
      setResultado(await chamar<ResultadoImportacao>('confirmar', corpo));
    } catch (e) {
      if (e instanceof ErroImportacao && e.status === 403) {
        setErro(mensagemDe(e, ''));
      } else if (e instanceof ErroImportacao && e.status === 409 && e.codigo === CODIGO_IMPORTACAO_JA_CONFIRMADA) {
        // A planilha já gerou PVs (duplo clique/reenvio): nada novo foi criado; mostra os PVs existentes.
        setErro(e.detalhe ?? 'Este arquivo já foi importado.');
        setPvsJaCriados(e.jaImportado);
      } else {
        // "Nada foi gravado" só com 4xx explícito; rede/5xx = resultado incerto (WR-D1-01).
        const status = e instanceof ErroImportacao ? e.status : null;
        setErro(mensagemFalhaConfirmacao(status, e instanceof ErroImportacao ? e.detalhe : null));
        setConferirPedidos(classificarFalhaConfirmacao(status) === 'INCERTO');
      }
    } finally {
      setOcupado(false);
    }
  }

  function alterarColuna(campo: CampoImportacaoPv, valor: string): void {
    setColunas((atual) => ({ ...atual, [campo]: valor }));
    setMapeamentoAplicado(false);
    setPrevia(null);
  }

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-6">
      <Card>
        <CardContent className="flex flex-col gap-4">
          <h1 className="text-xl font-semibold leading-tight">Importar pedido de compra</h1>
          <ol className="flex flex-wrap items-center gap-4" aria-label="Passos da importação">
            {PASSOS.map(({ numero, titulo }) => {
              const atual = !resultado && numero === passo;
              return (
                <li
                  key={numero}
                  aria-current={atual ? 'step' : undefined}
                  className={`border-b-2 pb-1 text-sm ${atual ? 'border-primary font-semibold text-primary' : 'border-transparent text-muted-foreground'}`}
                >
                  {`${numero} ${titulo}`}
                </li>
              );
            })}
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {resultado ? (
            <div className="flex flex-col items-start gap-3 py-4">
              <p role="status" className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="size-5 text-emerald-700" aria-hidden="true" />
                {rotuloSucessoImportacao({ pvs: resultado.pvsCriados.length, aguardandoVinculo: resultado.itensAguardandoVinculo })}
              </p>
              <Button asChild>
                <a href="/vendas/pedidos">Ver pedidos</a>
              </Button>
            </div>
          ) : passo === 1 ? (
            <PassoArquivo
              cliente={cliente}
              onCliente={(novo) => {
                setCliente(novo);
                invalidarLeitura();
              }}
              arquivo={arquivo}
              onArquivo={(novo) => void escolherArquivo(novo)}
              temCabecalho={temCabecalho}
              onTemCabecalho={(valor) => {
                setTemCabecalho(valor);
                invalidarLeitura();
              }}
              abas={abas}
              aba={aba}
              onAba={(nova) => {
                setAba(nova);
                invalidarLeitura();
              }}
              erro={erro}
              detalheErro={detalheErro}
              ocupado={ocupado}
              onContinuar={() => void continuarParaMapeamento()}
            />
          ) : passo === 2 && leitura ? (
            <PassoMapeamento
              clienteNome={cliente?.nome ?? ''}
              leitura={leitura}
              colunas={colunas}
              onColuna={alterarColuna}
              mapeamentoAplicado={mapeamentoAplicado}
              salvar={salvar}
              onSalvar={setSalvar}
              erro={erro}
              ocupado={ocupado}
              onVoltar={() => {
                limparErro();
                setPasso(1);
              }}
              onGerar={() => void gerarPrevia()}
            />
          ) : previa ? (
            <PassoPrevia
              previa={previa}
              ignorarErros={ignorarErros}
              onIgnorarErros={setIgnorarErros}
              apenasProblemas={apenasProblemas}
              onApenasProblemas={setApenasProblemas}
              erro={erro}
              pvsJaCriados={pvsJaCriados}
              conferirPedidos={conferirPedidos}
              ocupado={ocupado}
              onVoltar={() => {
                limparErro();
                setPasso(2);
              }}
              onConfirmar={() => void confirmar()}
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
